import React, { useCallback, useEffect, useState } from "react";
import { Boxes, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/Bits";
import { useBrand } from "@/context/BrandContext";
import { formatINR } from "@/lib/ui";
import api from "@/lib/api";

export default function Inventory() {
  const { selectedBrand, selectedHub } = useBrand();
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (selectedBrand && selectedBrand !== "All Brands") p.set("brand", selectedBrand);
    if (selectedHub && selectedHub !== "All Hubs") p.set("hub_id", selectedHub);
    api.get(`/inventory?${p.toString()}`).then((r) => setRows(r.data)).catch(() => {});
  }, [selectedBrand, selectedHub]);
  useEffect(() => { load(); }, [load]);

  const totalOnHand = rows.reduce((s, r) => s + (r.on_hand || 0), 0);
  const totalReserved = rows.reduce((s, r) => s + (r.reserved || 0), 0);
  const lowStock = rows.filter((r) => r.available <= (r.reorder_level || 5)).length;

  return (
    <div>
      <PageHeader title="Inventory" subtitle="Stock levels across all fulfillment hubs · reservations prevent overselling" />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[["SKUs stocked", rows.length, ""], ["On hand", totalOnHand, ""], ["Reserved", totalReserved, "text-[#FFD600]"], ["Low stock", lowStock, "text-[#FF6259]"]].map(([k, v, c]) => (
            <div key={k} className="bg-[#121212] border border-white/10 rounded-sm p-4">
              <div className={`font-display text-2xl font-bold ${c}`}>{v}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1">{k}</div>
            </div>
          ))}
        </div>
        {rows.length === 0 ? <EmptyState icon={Boxes} title="No inventory" hint="Add product offerings to hubs to track stock." /> : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-wider text-zinc-500 border-b border-white/10">
                <th className="px-4 py-3">Hub</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3 text-right">Transfer ₹</th><th className="px-4 py-3 text-right">On hand</th>
                <th className="px-4 py-3 text-right">Reserved</th><th className="px-4 py-3 text-right">Available</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const low = r.available <= (r.reorder_level || 5);
                  return (
                    <tr key={r.id} data-testid={`inv-row-${r.id}`} className="border-b border-white/5">
                      <td className="px-4 py-3 text-zinc-300">{r.hub_name}</td>
                      <td className="px-4 py-3 text-zinc-200">{r.product_name || "—"}</td>
                      <td className="px-4 py-3 font-mono text-zinc-500">{r.sku}</td>
                      <td className="px-4 py-3 text-right font-mono text-zinc-300">{formatINR(r.transfer_price)}</td>
                      <td className="px-4 py-3 text-right font-mono text-zinc-300">{r.on_hand}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#FFD600]">{r.reserved}</td>
                      <td className={`px-4 py-3 text-right font-mono flex items-center justify-end gap-1 ${low ? "text-[#FF6259]" : "text-[#00FF66]"}`}>
                        {low && <AlertTriangle size={12} />}{r.available}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
